import { ChangeDetectionStrategy, Component, effect, ElementRef, input, OnDestroy, viewChild } from '@angular/core';

import { formulaToLatex } from '../../utils/formula-to-latex';

/** MathJax window augmentation for type safety */
interface MathJaxConfig {
  tex?: { inlineMath?: string[][] };
  svg?: { fontCache?: string };
  startup?: {
    ready?: () => void;
    defaultReady?: () => void;
  };
  typesetPromise?: (elements: HTMLElement[]) => Promise<void>;
}

interface WindowWithMathJax {
  MathJax?: MathJaxConfig;
}

/**
 * Global MathJax typeset promise queue to avoid concurrent rendering.
 */
let mathjaxReady: Promise<void> | null = null;

/**
 * Loads MathJax 3 from CDN if not already loaded.
 */
function ensureMathJaxLoaded(): Promise<void> {
  if (mathjaxReady) {
    return mathjaxReady;
  }

  mathjaxReady = new Promise<void>((resolve) => {
    const win = window as unknown as WindowWithMathJax;

    // Check if MathJax is already available
    if (win.MathJax) {
      resolve();
      return;
    }

    // Configure MathJax before loading
    win.MathJax = {
      tex: { inlineMath: [['$', '$']] },
      svg: { fontCache: 'global' },
      startup: {
        ready: () => {
          const mj = win.MathJax;
          if (mj?.startup?.defaultReady) {
            mj.startup.defaultReady();
          }
          resolve();
        }
      }
    };

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js';
    script.async = true;
    document.head.appendChild(script);
  });

  return mathjaxReady;
}

@Component({
  selector: 'iot-platform-ui-formula-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: ` <div #mathContainer class="formula-preview-output" data-test="formula-preview"></div> `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }

    .formula-preview-output {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      min-height: 36px;
      padding: 4px 12px;
      border-radius: 8px;
      background: var(--mat-sys-surface, #ffffff);
      border: 1px solid var(--mat-sys-outline-variant, #c4c7c5);
      overflow-x: auto;
    }

    .formula-preview-output:empty::after {
      content: '—';
      color: var(--mat-sys-on-surface-variant, #49454f);
      opacity: 0.4;
      font-size: 13px;
    }
  `
})
export class FormulaPreviewComponent implements OnDestroy {
  /** The raw formula string to render */
  readonly formula = input<string>('');

  /** Variable names used for accurate LaTeX conversion via mathjs toTex() */
  readonly variables = input<string[]>([]);

  readonly mathContainer = viewChild.required<ElementRef<HTMLElement>>('mathContainer');

  private renderTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const formulaValue = this.formula();
      const variableNames = this.variables();
      this.scheduleRender(formulaValue, variableNames);
    });
  }

  ngOnDestroy(): void {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
  }

  private scheduleRender(formula: string, variableNames: string[]): void {
    // Debounce rendering to avoid excessive MathJax calls
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    this.renderTimeout = setTimeout(() => {
      void this.renderLatex(formula, variableNames);
    }, 150);
  }

  private async renderLatex(formula: string, variableNames: string[]): Promise<void> {
    const container = this.mathContainer()?.nativeElement;
    if (!container) {
      return;
    }

    const latex = formulaToLatex(formula, variableNames);
    if (!latex) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = `$${latex}$`;

    try {
      await ensureMathJaxLoaded();
      const win = window as unknown as WindowWithMathJax;
      if (win.MathJax?.typesetPromise) {
        await win.MathJax.typesetPromise([container]);
      }
    } catch {
      // MathJax rendering failed — show raw LaTeX as fallback
      container.textContent = latex;
    }
  }
}
